import { BehaviorSubject, Observable, Subject, of } from 'rxjs';

import { Inject, Injectable, isDevMode } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AiMonitoringService } from '../services/monitoring.service';
import { IDIRECTORYSERVICE, IDirectoryService } from '../services/idirectory.service';
import { AzureADOrg } from '../models/azureADOrg.model';
import { environment } from '../../environments/environment';
import { TenantInfo } from '../models/tenantInfo.model';
import { User } from '../models/user.model';
import { UserSettings } from '../models/usersettings.model';

export const DEFAULT_ADMIN_UNIT_ID = '10000000-1000-1000-1000-100000000000';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  url = environment.portalapiurl + '/Users/GetUserSettings';

  public orgData: any = null;

  public isLoaded: boolean;
  public portaluser: UserSettings;
  public portaluserLatest: Subject<UserSettings> = new BehaviorSubject<UserSettings>(undefined);
  public totaluserLatest: Subject<User> = new BehaviorSubject<User>(undefined);

  public navStart: BehaviorSubject<string> = new BehaviorSubject<string>('');
  public isLoginDebug = false;

  public customerStatus = '';
  public adType = sessionStorage.getItem('adType');
  public tenants: Subject<TenantInfo[]> = new BehaviorSubject<TenantInfo[]>(undefined);
  public bUserLatestUpdated = false;

  constructor(
    @Inject(IDIRECTORYSERVICE) public directoryService: IDirectoryService,
    private http: HttpClient,
    private aiService: AiMonitoringService
  ) {
    this.isLoaded = false;
    this.aiService.xwareLogDebug('LoginService:init');

    this.checkDebug();

    if (!this.directoryService.directoryUrl) {
      this.directoryService.directoryUrl = environment.portalapiurl + '/directory';
    }
    if (isDevMode() && this.isLoginDebug) {
      this.aiService.xwareLogDebug('I am in debug');
      this.runDebugProcesses();
    } else {
      this.getAzureADFields();
    }
  }

  loggedIn: boolean;
  public userInfo: any = null;
  public isIframe: boolean;

  public getErrorPageUrl(): string {
    return `${window.location.origin}/assets/errorpage.html`;
  }

  public goToErrorPage(): void {
    window.location.href = this.getErrorPageUrl();
  }

  initialProceses() {}

  private static azureADFieldsTriggered = false;

  public getAzureADFields() {
    this.aiService.xwareLogDebug('LoginService:getAzureADFields:starting');
    if (LoginService.azureADFieldsTriggered) {
      return;
    }
    LoginService.azureADFieldsTriggered = true;
    this.directoryService.getOrg().subscribe(
      ogData => {
        try {
          this.orgData = ogData;

          if (this.orgData.value.length === 0) {
            this.aiService.xwareLogEvent('REDIRECTINVALID');
            this.navStart.next('invalid');
          } else {
            this.directoryService.getUserInfo().subscribe(
              data => {
                try {
                  if (data) {
                    sessionStorage.setItem('user_id', data.id);
                    let tenant = '';
                    if (this.adType === 'gapi') {
                      tenant = this.orgData.value[0].id;
                    } else {
                      tenant = this.getTenantNameFromDomains(this.orgData);
                    }

                    this.buildTenantsArray(this.orgData);

                    if (tenant === '' || tenant === null) {
                      this.aiService.xwareLogEvent('NOTENANTDATA');
                      this.navStart.next('error');
                    } else {
                      const curUser = this.createUserObject(
                        data.userPrincipalName,
                        tenant,
                        this.orgData.value[0].displayName,
                        data.givenName,
                        data.surname,
                        this.orgData.value[0].id
                      );
                      this.checkUser(curUser);
                    }
                  } else {
                    this.aiService.xwareLogEvent('NOADFIELDS');
                  }
                } catch (error) {
                  this.aiService.xwareLogErrorStack(
                    'LoginService:getAzureADFields:getUserInfo:' + error
                  );
                  this.aiService.xwareLogEvent('REDIRECTERROR');
                  this.navStart.next('error');
                }
              },
              error => {
                this.aiService.xwareLogErrorStack(
                  'LoginService:getAzureADFields:getUserInfo:' + error
                );
              }
            );
          }
        } catch (error) {
          LoginService.azureADFieldsTriggered = false;
          this.aiService.xwareLogErrorStack('LoginService:getAzureADFields:getOrg:' + error);
          this.aiService.xwareLogEvent('REDIRECTERROR');
          this.navStart.next('error');
        }
      },
      error => {
        LoginService.azureADFieldsTriggered = false;
        const arrSplit = environment.redirectUri.split('/');
        this.aiService.xwareLogErrorStack(
          'LoginService:LogUserToOrg:getOrg:' + JSON.stringify(error)
        );

        if (this.adType === 'gapi' && error.status === 401) {
          window.location.href = arrSplit[0] + '//' + arrSplit[2] + '/assets/infopage.html';
        } else {
          this.goToErrorPage();
        }
      }
    );
  }

  getTenantNameFromDomains(orgData: AzureADOrg): string {
    try {
      return orgData.value[0].verifiedDomains.find(x => x.isInitial === true).name;
    } catch (e) {
      this.aiService.xwareLogErrorStack('Error calculating tenant name from Domain: ' + e);
      return '';
    }
  }

  buildTenantsArray(orgData: AzureADOrg) {
    const tenant = new TenantInfo();
    const arrTenants = [];

    try {
      const dom = orgData.value[0].verifiedDomains.find(x => x.isInitial === true);
      tenant.tenantName = dom.name;
      tenant.tenantID = orgData.value[0].id;
      arrTenants.push(tenant);
      this.tenants.next(arrTenants);
    } catch (e) {
      this.aiService.xwareLogErrorStack('Error calculating tenant name from Domain: ' + e);
      return '';
    }
  }

  private createUserObject(
    userName: string,
    tenantName: string,
    orgName: string,
    givenName: string,
    surname: string,
    tenantId: string
  ): User {
    this.aiService.xwareLogDebug('LoginService:createUserObject');
    const user = new User();

    user.upn = userName;
    user.tenantName = tenantName;
    user.orgName = orgName;
    user.givenName = givenName;
    user.surname = surname;
    user.tenantId = tenantId;

    sessionStorage.setItem('tenantName', tenantName);
    sessionStorage.setItem('tenantId', tenantId);
    sessionStorage.setItem('userPrincipalName', userName);
    sessionStorage.setItem('company', orgName);
    return user;
  }

  checkUser(user: User) {
    let bIsExpired = false;
    this.aiService.xwareLogDebug('LoginService:checkingUser' + JSON.stringify(user));

    this.addUser(user).subscribe(
      x => {
        this.aiService.xwareLogDebug('LoginService:checkUser:got data:' + JSON.stringify(x));
        this.portaluser = x;
        user.settings = this.portaluser;
        this.applySessionAdminUnitId(this.portaluser);

        if (x == null || (x.deploymentType === null && x.customerStatus === null)) {
          if (this.adType === 'gapi') {
            this.navStart.next('manualsetup');
            this.aiService.xwareLogEvent('REDIRECT Manual onboarding request');
          } else {
            this.navStart.next('setup');
            this.aiService.xwareLogEvent('REDIRECTSETUP');
          }
        } else {
          if (x.customerStatus != null) {
            this.customerStatus = x.customerStatus.toLowerCase();
            if (x.customerStatus.toLowerCase() !== 'active') {
              bIsExpired = true;
              sessionStorage.setItem('expiryType', x.customerStatus.toLowerCase());
              this.aiService.xwareLogEvent('REDIRECTEXPIRED');
              this.navStart.next('expired');
            }
          }
          if (x.deploymentType != null) {
            sessionStorage.setItem('deploymentType', x.deploymentType.toLowerCase());
          }

          if (!bIsExpired) {
            this.aiService.xwareLogDebug(
              'LoginService:not expired check if customer ' + this.customerStatus
            );

            if (this.customerStatus !== 'active' && !x.isSMTPKeyAccepted) {
              this.aiService.xwareLogDebug('LoginService:not a customer, go to onboarding');
              this.aiService.xwareLogEvent('REDIRECTSETUP');
              this.navStart.next('setup');
            } else {
              this.aiService.xwareLogDebug('LoginService: active customer, loading home');
              this.aiService.xwareLogEvent('REDIRECHOME');
              this.navStart.next('home');
            }
          }
        }

        this.aiService.xwareLogDebug(
          'LoginService:push user into portaluserLatest' + JSON.stringify(this.portaluser)
        );
        if (!this.bUserLatestUpdated) {
          this.portaluserLatest.next(this.portaluser);
          this.bUserLatestUpdated = true;
        }

        this.totaluserLatest.next(user);
        if (this.portaluser?.allowNewDesigner) {
          sessionStorage.setItem('allowNewDesigner', 'Y');
        }

        this.isLoaded = true;
      },
      err => {
        (this.aiService.xwareLogErrorStack('LoginService:LogLogin:Error:' + err),
          this.navStart.next('error'));
      },
      () => this.aiService.xwareLogDebug('LoginService:LogLogin:Completed')
    );
  }

  applySessionAdminUnitId(settings: UserSettings | null): void {
    const adminUnitId = this.resolveAdminUnitId(settings);
    sessionStorage.setItem('AUId', adminUnitId);
    this.aiService.xwareLogDebug('LoginService:applySessionAdminUnitId: AUId set to:' + adminUnitId);
  }

  resolveAdminUnitId(settings: UserSettings | null | undefined): string {
    if (settings?.dBVersion === 2) {
      const unit = settings.adminUnits?.[0];
      const adminUnitId = unit?.adminUnitId || unit?.id;
      if (adminUnitId) {
        return adminUnitId;
      }
    }
    return DEFAULT_ADMIN_UNIT_ID;
  }

  addUser(user: User): Observable<UserSettings> {
    const strUrl = this.url;
    this.aiService.xwareLogDebug('LoginService:addUser' + JSON.stringify(user));

    return this.http.post<UserSettings>(strUrl, user).pipe(
      tap(() => this.aiService.xwareLogDebug('User added')),
      catchError(err => {
        this.aiService.xwareLogErrorStack('LoginService:add:error:' + JSON.stringify(err));
        this.navStart.next('error');
        return of(null);
      })
    );
  }

  checkDebug() {
    this.isLoginDebug = false;
    if (localStorage.getItem('loginservice') === 'debug') {
      this.aiService.xwareLogDebug('*** Debug mode is ON ***');
      this.isLoginDebug = true;
    } else {
      this.isLoginDebug = false;
    }
  }

  runDebugProcesses() {}
}
